import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { CLASS_TYPES, STUDIO, VIDEOS } from '../data/mock';
import { usePageMeta } from '../components/Layout';
import { Reveal, SectionHeader, PageHero, CloseCTA, ProofStrip, PitVideo, VideoSeo } from '../components/Shared';

const SPLIT = [
  { day: 'Monday', focus: 'Total Body Sculpt — Upper Body Focus', desc: 'Full-body sculpt work with the volume tilted to chest, back, shoulders and arms.' },
  { day: 'Tuesday', focus: 'Lower Body Strength & Abs', desc: 'Squats, lunges, sled pushes and core finishers. Leg day, amplified.' },
  { day: 'Wednesday', focus: 'Upper Body Arms & Abs', desc: 'Pressing, pulling and arm-focused strength stitched to core circuits.' },
  { day: 'Thursday', focus: 'Total Body Sculpt — Lower Body Focus', desc: 'The sculpt formula flipped — glutes, quads and hamstrings take the lead.' },
  { day: 'Friday', focus: 'F*ck It Up Friday', desc: 'Max-effort conditioning. The class members call “the honest hour.”' },
  { day: 'Saturday', focus: 'Total Body', desc: 'The complete rotation — every block, every muscle group.' },
  { day: 'Sunday', focus: 'Total Body Circuit', desc: 'A circuit-style total body to close the week — every station, one flow.' },
];

export default function Workout() {
  usePageMeta(
    'The Workout — Non-Stop HIIT Classes',
    'What is the PITFIT workout? Non-stop, coached 45- and 60-minute HIIT classes in Dallas rotating strength floor work, conditioning circuits and sculpt blocks.'
  );

  return (
    <>
      <VideoSeo keys={['garage', 'wallball']} />
      <PageHero
        tag="The PITFIT Method"
        title="The non-stop HIIT workout"
        sub="45 or 60 minutes. Zero downtime. A coached rotation through strength, conditioning and sculpt blocks — programmed fresh every week by founder Chris Wiese and his coaching team."
        video={VIDEOS.garage}
      />
      <ProofStrip />

      {/* WHAT IS HIIT */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <p className="mono-label mb-4">First Principles</p>
            <h2 className="h-section text-white">What is HIIT — and why does it work?</h2>
            <p className="text-muted-pf mt-5 text-[17px] leading-relaxed">
              High-Intensity Interval Training alternates short bursts of maximum effort with brief recovery windows. Your heart rate spikes, recovers, and spikes again — which burns more calories per minute than steady-state cardio and triggers an afterburn effect (EPOC) that keeps your metabolism elevated for hours after class.
            </p>
            <p className="text-muted-pf mt-4 text-[17px] leading-relaxed">
              At PITFIT, the intervals are programmed for you. You never plan a workout, never guess a weight, never wander a gym floor. You show up, the coach runs the class, and the format guarantees intensity — scaled to your level, because you control your pace and your loads.
            </p>
            <div className="grid grid-cols-3 gap-4 mt-8">
              {[['45–60', 'minutes non-stop'], ['500–900', 'calories burned'], ['16–28', 'people per class']].map(([n, l]) => (
                <div key={l} className="glass-card p-4 text-center">
                  <p className="font-display font-bold text-2xl text-white">{n}</p>
                  <p className="font-mono-pf text-[10.5px] uppercase tracking-[0.1em] text-muted-pf mt-1">{l}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="rounded-2xl w-full h-full min-h-[380px] overflow-hidden border border-[rgba(148,170,220,0.16)]">
              <PitVideo video={VIDEOS.wallball} className="w-full h-full min-h-[380px] object-cover" label="Wall-ball strength work during a PITFIT class" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* WEEKLY SPLIT */}
      <section className="section-pad" style={{ background: 'var(--pf-bg2)' }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader tag="The Weekly Split" title="Programmed so you can come every day." sub="No muscle group gets hammered two days in a row. Monday’s soreness recovers while Tuesday earns its own." />
          <div className="space-y-3">
            {SPLIT.map((s, i) => (
              <Reveal key={s.day} delay={i * 60}>
                <div className="glass-card glass-card-hover px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-8">
                  <span className="font-mono-pf text-[12px] uppercase tracking-[0.14em] w-28 shrink-0" style={{ color: 'var(--pf-brand2)' }}>{s.day}</span>
                  <span className="font-display font-semibold text-white text-lg w-56 shrink-0">{s.focus}</span>
                  <span className="text-muted-pf text-[15px]">{s.desc}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-9">
            <Link to="/hiit-workout/schedule" className="btn-primary">See This Week's Schedule <ArrowRight size={16} /></Link>
            <Link to="/hiit-workout/beginners" className="btn-ghost">Is HIIT right for me?</Link>
          </div>
        </div>
      </section>

      {/* CLASS TYPES */}
      <section className="section-pad" style={{ background: 'var(--pf-bg2)' }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader tag="Class Types" title="Know your enemy." />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {Object.entries(CLASS_TYPES).map(([name, desc], i) => (
              <Reveal key={name} delay={i * 70}>
                <div className="glass-card glass-card-hover p-7">
                  <h3 className="font-display font-semibold text-white text-xl">{name}</h3>
                  <p className="text-muted-pf text-[15px] mt-2.5">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="text-muted-pf text-[15px] mt-8">
            Who is it for? Anyone willing to work. Every movement has a beginner option, every block scales to your level, and with intentionally sized classes, your coach is watching your form the whole hour. New to HIIT? Start with <Link to="/hiit-workout/first-class" className="underline underline-offset-4" style={{ color: 'var(--pf-brand2)' }}>your first class guide</Link> or read the map of <Link to="/blog/pitfit-class-guide" className="underline underline-offset-4" style={{ color: 'var(--pf-brand2)' }}>every PITFIT class explained</Link>. Training for a race? See how <Link to="/blog/hyrox-vs-hiit" className="underline underline-offset-4" style={{ color: 'var(--pf-brand2)' }}>HYROX and HIIT compare</Link> or explore <Link to="/hyrox-classes-dallas" className="underline underline-offset-4" style={{ color: 'var(--pf-brand2)' }}>HYROX classes in Dallas</Link>.
          </p>
        </div>
      </section>

      <CloseCTA title="Feel it once. You'll understand." sub="Your first class in the PIT costs $15 — no membership, no commitment. Just show up ready to work." />
    </>
  );
}
